import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Fingerprint, ShieldCheck, Radio, UploadCloud, Loader2 } from "lucide-react";
import axios, { API } from "@/lib/apiClient";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const VERDICT_COPY = {
  MAISON_VERIFIED: { label: "Maison-Verified Original", color: "#22C55E" },
  SYNTHETIC_DETECTED: { label: "AI-Synthetic Potential", color: "#EF4444" },
  INCONCLUSIVE: { label: "Inconclusive", color: "#F59E0B" },
};

export const VerifyExecutive = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/verify/${slug}/public`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const submitClip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setResult(null);
    const form = new FormData();
    form.append("slug", slug);
    form.append("file", file);
    try {
      const { data: res } = await axios.post(`${API}/verify/identity`, form);
      setResult(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not analyze this clip.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center" data-testid="verify-executive-not-found">
        <p className="text-slate-400">No Sovereign Identity Shield record found for this link.</p>
      </div>
    );
  }

  if (!data) {
    return <div className="mx-auto max-w-2xl px-6 py-24 text-center text-slate-500" data-testid="verify-executive-loading">Loading Sovereign Identity Shield...</div>;
  }

  const verdict = result ? VERDICT_COPY[result.verdict] || VERDICT_COPY.INCONCLUSIVE : null;

  return (
    <div className="mx-auto max-w-2xl px-6 py-16" data-testid="verify-executive-page">
      <div className="mb-2 flex items-center gap-2">
        <Fingerprint className="h-6 w-6 text-[#D4AF37]" />
        <h1 className="font-heading text-3xl font-bold text-white">{data.executive_name}</h1>
      </div>
      <p className="mb-6 text-slate-400">{data.brand_name} &middot; Sovereign Identity Shield</p>

      {data.proof_of_life?.active && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-truth-green/40 bg-truth-green/5 p-4" data-testid="proof-of-life-banner">
          <Radio className="h-5 w-5 flex-shrink-0 text-truth-green" />
          <div>
            <p className="text-sm font-semibold text-truth-green">Public Proof of Life &mdash; Active</p>
            <p className="text-xs text-slate-500">Confirmed authentic by {data.executive_name} on {new Date(data.proof_of_life.issued_at).toLocaleString()}</p>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6" data-testid="verify-upload-card">
        <p className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-white"><ShieldCheck className="h-5 w-5 text-[#D4AF37]" /> Verify a Clip</p>
        <p className="mb-4 text-sm text-slate-400">
          Upload a photo, audio, or video clip claiming to be {data.executive_name}. We compare it against the registered Master DNA and return a clinical verdict.
        </p>
        <input ref={fileInputRef} type="file" accept="audio/*,video/*,image/*" onChange={submitClip} className="hidden" data-testid="verify-file-input" />
        <Button disabled={busy} onClick={() => fileInputRef.current?.click()} className="gap-2 bg-[#D4AF37] text-obsidian hover:bg-[#D4AF37]/90" data-testid="verify-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {busy ? "Analyzing..." : "Upload & Verify"}
        </Button>
      </div>

      {result && (
        <div className="mt-6 rounded-lg border-2 p-6" style={{ borderColor: verdict.color }} data-testid="verify-result-card">
          <p className="mb-2 font-heading text-2xl font-bold" style={{ color: verdict.color }} data-testid="verify-verdict-label">{verdict.label}</p>
          {result.combined_score !== null && (
            <p className="mb-3 text-sm text-slate-400" data-testid="verify-combined-score">Combined similarity score: {result.combined_score}/100</p>
          )}
          <p className="text-sm text-slate-300" data-testid="verify-narrative">{result.narrative}</p>
          <p className="mt-4 text-xs text-slate-600">Best-effort forensic signal &mdash; not a certified or legal-grade deepfake determination.</p>
        </div>
      )}
    </div>
  );
};
